import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { fadeAnimation } from 'src/app/animations/fade-animations';
import { PagedData } from 'src/app/models/page-data';
import { SearchService } from 'src/app/services/search/search.service';
import { UtilService } from 'src/app/services/util/util.service';

import { TabChangeEvent, pgTabSetComponent } from '../tabs/tabset.component';
import { MenuService } from './../../services/menu/menu.service';
import { ConfirmModalComponent } from './../confirm-modal/confirm-modal.component';
import { IconComponent } from '../icon/icon.component';
import { FormsModule } from '@angular/forms';
import { pgTabComponent } from '../tabs/tab.component';
import { MatProgressBar } from '@angular/material/progress-bar';
import { CdkTable, CdkColumnDef, CdkCellDef, CdkCell, CdkRowDef, CdkRow } from '@angular/cdk/table';
import { SvgIconComponent } from 'angular-svg-icon';
import { SpanUnsafeHtmlComponent } from '../span-unsafe-html/span-unsafe-html.component';
import { MatPaginator } from '@angular/material/paginator';
import { UserDisplayComponent } from '../user-display/user-display.component';
import { ConfirmModalComponent as ConfirmModalComponent_1 } from '../confirm-modal/confirm-modal.component';
import { DatePipe , CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { DateAgoPipe } from 'src/app/pipes/date-ago.pipe';
import { CapitalizeNamePipe } from 'src/app/pipes/capitalize-name/capitalize-name.pipe';

@Component({
    selector: 'app-search-overlay',
    templateUrl: './search-overlay.component.html',
    animations: [fadeAnimation],
    styleUrls: ['./search-overlay.component.scss'],
    imports: [CommonModule, IconComponent, FormsModule, pgTabSetComponent, pgTabComponent, MatProgressBar, CdkTable, CdkColumnDef, CdkCellDef, CdkCell, SvgIconComponent, SpanUnsafeHtmlComponent, CdkRowDef, CdkRow, MatPaginator, UserDisplayComponent, ConfirmModalComponent_1, DatePipe, TranslatePipe, DateAgoPipe, CapitalizeNamePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchOverlayComponent implements OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  searchService = inject(SearchService);
  util = inject(UtilService);
  router = inject(Router);
  menu = inject(MenuService);

  @ViewChild('searchField', { static: false }) searchField: ElementRef;
  @ViewChild('confirmModal') confirmModal: ConfirmModalComponent;
  isVisible = false;
  value = '';
  path = '';
  enabled = false;
  toggleSubscription: Subscription;
  availablePodList: any = [];
  podList: any = [];
  podSearchableList: any = { beezz: false, workGroup: false, news: false, events: false, video: false, poll: false, cms: false, wiki: false, blog: false, podcast: false, directory: false, training: false, ideas: false };
  modelChanged: Subject<string> = new Subject<string>();
  resourceType: any;
  sortType = 'result';
  rows = new Array<any>();
  loading = false;
  exportExcelProgress = false;
  manager = false;
  modalMessage = '';
  searchDisplayedColumns: string[] = ['title'];
  emptyMessage = {
    emptyMessage: 'Nenhum resultado encontrado com o termo pesquisado.'
  };

  constructor() {
    const menu = this.menu;

    this.util.getTranslations(['Nenhum resultado encontrado com o termo pesquisado.']).subscribe((data: any) => {
      this.emptyMessage = { emptyMessage: data[0] };
      this.cdr.markForCheck();
    });
    this.util.getBasicOperation('MANAGER_SEARCH').subscribe((data: any) => { this.manager = data; this.cdr.markForCheck(); });
    this.searchService.page.pageNumber = 0;
    this.searchService.page.size = 20;
    this.toggleSubscription = this.searchService.searchToggle.subscribe((toggleValue: any) => {
      this.open(toggleValue.text);
      this.doSearch();
      this.cdr.markForCheck();
    });

    this.menu.getLoggedUser().then((data: any) => {
      if (menu?.loggedUser?.email) {
        this.modalMessage = 'O sistema enviará o relatório para o seu e-mail cadastrado. Você confirma essa ação?';
      } else {
        this.modalMessage = 'Você deve ter um email cadastrado para receber o relatório.';
      }
      this.cdr.markForCheck();
    });

    this.modelChanged.pipe(debounceTime(1500), distinctUntilChanged()).subscribe((model: any) => {
      this.value = model;
      this.path = this.value.replace(/[\n\r]+/g, '').replace('#', '').toLowerCase();
      this.doSearch();
      this.cdr.markForCheck();
    });
  }

  @Input() set isEnabled(isEnabled: boolean) {
    this.isEnabled = isEnabled;
  }
  get isEnabled() {
    return this.enabled;
  }

  ngOnDestroy() {
    this.toggleSubscription.unsubscribe();
  }

  open(text?) {
    this.isVisible = true;
    if (text) {
      this.value = text;
    } else if (!this.value) {
      this.value = '';
    }

    this.loadPods(JSON.parse(localStorage.getItem('menu')));
    setTimeout(() => {
      this.searchField.nativeElement.focus();
    }, 200);
  }

  searchKeyPress(event) {
    if (this.isVisible && event.keyCode === 27) {
      this.isVisible = false;
      this.value = '';
    }
  }

  changed(text: string) {
    this.searchService.page.pageNumber = 0;
    this.modelChanged.next(text);
  }

  close($event) {
    $event.preventDefault();
    this.rows = new Array<any>();
    this.isVisible = false;
    this.value = '';
    this.searchService.searchQuery = null;
    this.searchService.searchResult = [];
    this.searchService.allLoaded = false;
    this.searchService.page.pageNumber = 0;
    this.searchService.page.size = 20;
  }

  isOverlayVisible() {
    return this.isVisible ? true : false;
  }

  loadPods(data: any) {
    this.availablePodList = ['beezz', 'workGroup', 'news', 'events', 'video', 'poll', 'cms', 'wiki', 'blog', 'podcast', 'directory', 'training', 'ideas'];
    if (data) {
      data.forEach(element => {
        element.podTOs.map((pod) => {
          this.podList.push(pod);
        });
      });
      for (const pod of this.podList) {
        if (pod.podType === 'beezz' || pod.podType === 'news' || pod.podType === 'events' || pod.podType === 'video' || pod.podType === 'poll' ||
          pod.podType === 'cms' || pod.podType === 'wiki' || pod.podType === 'blog' || pod.podType === 'podcast' || pod.podType === 'directory' || pod.podType === 'training' || pod.podType === 'ideas' || pod.podType === 'workGroup') {
          this.podSearchableList[pod.podType] = true;
        }
      }
      for (const podTypeKey in this.podSearchableList) {
        if (this.podSearchableList[podTypeKey] === false) {
          this.availablePodList.splice(this.availablePodList.indexOf(podTypeKey), 1);
        }
      }
    }
  }

  doSearch() {
    if (this.value.toString()) {
      this.loading = true;
      this.searchService.search(this.searchService.page, this.value.toString(), this.resourceType, true, this.sortType).subscribe(pagedData => {
        this.loading = false;
        this.searchService.page = pagedData.page;
        this.rows = pagedData.data;
        this.cdr.markForCheck();
      });
    }
  }

  changePod(evt: TabChangeEvent) {
    this.searchService.pagedData = new PagedData();
    this.rows = new Array<any>();
    this.searchService.page.pageNumber = 0;
    this.searchService.page.size = 20;

    if (evt.index === 0) {
      this.resourceType = null;
    } else {
      this.resourceType = this.availablePodList[evt.index - 1];
    }

    this.setPage({ offset: 0 });
  }

  setPage(pageInfo) {
    if (this.value !== '') {
      this.searchService.page.pageNumber = pageInfo.pageIndex ?? pageInfo.offset ?? 0;
      if (pageInfo.pageSize) this.searchService.page.size = pageInfo.pageSize;
      this.doSearch();
    }
  }

  redirect(row) {
    let url = '';
    if (row.resourceType === 'beezz') {
      url = '/beezz/' + row.id + '/detail';

      if (row.workGroup?.id) {
        url = '/workGroup/' + row.workGroup.id + '/detail/beezz/' + row.id;
      }

    } else if (row.resourceType === 'news') {
      url = '/news/' + row.id + '/detail';
    } else if (row.resourceType === 'events') {
      url = '/events/' + row.id + '/detail';
    } else if (row.resourceType === 'video') {
      url = '/video/' + row.id + '/detail';
    } else if (row.resourceType === 'poll') {
      url = '/poll/' + row.id + '/detail';
    } else if (row.resourceType === 'cms') {
      url = '/cms/' + row.id + '/detail';
    } else if (row.resourceType === 'wiki') {
      url = '/wiki/' + row.id + '/detail';
    } else if (row.resourceType === 'blogpost') {
      url = '/blog/' + row.parentId + '/post/' + row.id;
    } else if (row.resourceType === 'podcast' && row.parentId == 0) {
      url = '/podcast/' + row.id + '/detail';
    } else if (row.resourceType === 'podcastEpisode' && row.parentId > 0) {
      url = '/podcast/' + row.parentId + '/episode/' + row.id + '/detail';
    } else if (row.resourceType === 'directory') {
      url = '/directory/' + row.id + '/profile';
    } else if (row.resourceType === 'training') {
      url = '/training/' + row.id + '/detail';
    } else if (row.resourceType === 'ideas') {
      url = '/ideas/' + row.id + '/detail';
    } else if (row.resourceType === 'ideasCategory') {
      url = '/ideas/campaign/' + row.id + '/detail';
    }

    return url;
    // this.router.navigateByUrl(url);
  }

  showModal() {
    if (this.manager) {
      this.confirmModal.show();
    }
  }

  setSearchSort(type) {
    this.searchService.pagedData = new PagedData();
    this.rows = new Array<any>();
    this.searchService.page.pageNumber = 0;
    this.searchService.page.size = 20;
    if (type === '') {
      this.sortType = null;
    } else {
      this.sortType = type;
    }
    this.doSearch();
  }

  exportResults() {
    if (this.manager) {
      this.exportExcelProgress = true;
      this.searchService.exportReport(this.value.toString(), this.resourceType ? this.resourceType : null, true).subscribe(data => {
        this.confirmModal.hide();
        this.exportExcelProgress = false;
        this.cdr.markForCheck();
      }, err => {
        this.confirmModal.deleteError();
        this.exportExcelProgress = false;
        this.cdr.markForCheck();
      });
    }
  }

}
